import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type AccountTargetListsResult,
  type ApiError,
  type ListTargetListsResult,
  type TargetOverviewResult,
} from '@whinerp/client';
import { formatMoney, formatRelativeDate } from '@whinerp/contracts/format';
import { Button, Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';

/**
 * Account-based marketing (G-177). On the marketing screen, the list both
 * teams work from: each target company, how many of its people engaged in the
 * last 30 days, and an audience of their people. On a company, the lists it
 * is on.
 */

const INPUT = 'h-input rounded-control border border-control bg-surface px-2 text-body text-ink';

export function TargetAccountsPanel(): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const queryClient = useQueryClient();
  const { can } = useSession();
  const enabled = useEntitlement('F-062').enabled && can('analytics.view');
  const lists = useQuery<ListTargetListsResult, ApiError>({ queryKey: ['target-lists'], queryFn: () => api.listTargetLists({}), enabled });
  const [chosen, setChosen] = useState<string | null>(null);
  const [name, setName] = useState('');
  const listId = chosen ?? lists.data?.lists[0]?.listId ?? null;
  const overview = useQuery<TargetOverviewResult, ApiError>({
    queryKey: ['target-overview', listId],
    queryFn: () => api.targetOverview({ path: { listId: listId! } }),
    enabled: enabled && listId !== null,
  });
  const create = useMutation({
    mutationFn: () => api.createTargetList({ body: { name } }),
    onSuccess: (list) => {
      setName('');
      setChosen(list.listId);
      void queryClient.invalidateQueries({ queryKey: ['target-lists'] });
    },
  });
  const audience = useMutation({ mutationFn: (maxTier: number) => api.targetAudience({ path: { listId: listId! }, body: { maxTier } }) });

  if (!enabled || !lists.data) return null;
  return (
    <Panel data-region="target-accounts">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <SectionLabel>{t('abm.title')}</SectionLabel>
          {lists.data.lists.length > 0 ? (
            <select className={`${INPUT} ms-auto`} data-target-list aria-label={t('abm.list')} value={listId ?? ''} onChange={(event) => setChosen(event.target.value)}>
              {lists.data.lists.map((list) => <option key={list.listId} value={list.listId}>{list.name} ({list.accounts})</option>)}
            </select>
          ) : null}
        </div>
        {overview.data && overview.data.accounts.length > 0 ? (
          <table className="w-full text-dense" data-target-overview>
            <thead>
              <tr className="text-meta text-ink-meta">
                <th className="p-1 text-start font-normal">{t('abm.company')}</th>
                <th className="p-1 text-end font-normal">{t('abm.tier')}</th>
                <th className="p-1 text-end font-normal">{t('abm.engaged')}</th>
                <th className="p-1 text-end font-normal">{t('abm.lastTouch')}</th>
                <th className="p-1 text-end font-normal">{t('abm.pipeline')}</th>
              </tr>
            </thead>
            <tbody>
              {overview.data.accounts.map((row) => (
                <tr key={row.accountId} data-target={row.accountId}>
                  <td className="p-1 text-ink">{row.name}{row.ownerName ? <span className="ms-2 text-meta text-ink-meta">{row.ownerName}</span> : null}</td>
                  <td className="p-1 text-end">{row.tier}</td>
                  <td className="p-1 text-end" data-target-engaged>{t('abm.ofPeople', { values: { engaged: String(row.engagedPeople), people: String(row.people) } })}</td>
                  <td className="p-1 text-end text-ink-600">{row.lastTouchAt ? formatRelativeDate(row.lastTouchAt, format) : t('abm.never')}</td>
                  <td className="p-1 text-end">{formatMoney(row.openPipeline, format, { currency: overview.data.currency })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : listId ? (
          <p className="text-meta text-ink-meta">{t('abm.emptyList')}</p>
        ) : null}
        {listId && can('campaign.send') ? (
          <div className="flex flex-wrap items-center gap-2">
            {[1, 2, 3].map((tier) => (
              <Button key={tier} size="small" data-target-audience={tier} disabled={audience.isPending} onClick={() => audience.mutate(tier)}>
                {t(`abm.audience.${String(tier) as '1'}`)}
              </Button>
            ))}
            {audience.data ? <span className="text-dense text-ink" data-target-audience-made>{t('abm.audienceMade', { values: { count: String(audience.data.members) } })}</span> : null}
          </div>
        ) : null}
        {can('campaign.send') ? (
          <form className="flex flex-wrap items-end gap-2" data-target-list-form
            onSubmit={(event) => {
              event.preventDefault();
              create.mutate();
            }}>
            <input className={INPUT} required maxLength={120} data-target-list-name placeholder={t('abm.newList')} value={name} onChange={(event) => setName(event.target.value)} />
            <Button size="small" type="submit" data-target-list-create disabled={create.isPending}>{t('abm.create')}</Button>
          </form>
        ) : null}
        {[create.error, audience.error].map((error, index) => (error ? <p key={index} className="text-dense text-danger-text">{errorText(error as ApiError)}</p> : null))}
      </div>
    </Panel>
  );
}

export function AccountTargetPanel({ accountId }: { accountId: string }): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const { can } = useSession();
  const enabled = useEntitlement('F-062').enabled;
  const on = useQuery<AccountTargetListsResult, ApiError>({
    queryKey: ['account-target-lists', accountId],
    queryFn: () => api.accountTargetLists({ path: { accountId } }),
    enabled,
  });
  const lists = useQuery<ListTargetListsResult, ApiError>({ queryKey: ['target-lists'], queryFn: () => api.listTargetLists({}), enabled });
  const [draft, setDraft] = useState({ listId: '', tier: '2' });
  const add = useMutation({
    mutationFn: () => api.targetAccount({ path: { listId: draft.listId }, body: { accountId, tier: Number(draft.tier) } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['account-target-lists', accountId] });
      void queryClient.invalidateQueries({ queryKey: ['target-lists'] });
    },
  });
  if (!enabled || !on.data || !lists.data || lists.data.lists.length === 0) return null;
  return (
    <Panel data-region="account-targets">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('abm.onLists')}</SectionLabel>
        {on.data.lists.length === 0 ? (
          <p className="text-meta text-ink-meta">{t('abm.notTargeted')}</p>
        ) : (
          <ul className="flex flex-wrap gap-2 text-dense">
            {on.data.lists.map((list) => (
              <li key={list.listId} data-account-target={list.listId} className="rounded-chip bg-sunken px-2 py-1 text-ink">
                {list.name} · {t('abm.tierN', { values: { tier: String(list.tier) } })}
              </li>
            ))}
          </ul>
        )}
        {can('campaign.send') ? (
          <form className="flex flex-wrap items-end gap-2" data-account-target-form
            onSubmit={(event) => {
              event.preventDefault();
              add.mutate();
            }}>
            <select className={INPUT} required data-account-target-list aria-label={t('abm.list')} value={draft.listId} onChange={(event) => setDraft({ ...draft, listId: event.target.value })}>
              <option value="">{t('abm.chooseList')}</option>
              {lists.data.lists.map((list) => <option key={list.listId} value={list.listId}>{list.name}</option>)}
            </select>
            <select className={INPUT} data-account-target-tier aria-label={t('abm.tier')} value={draft.tier} onChange={(event) => setDraft({ ...draft, tier: event.target.value })}>
              {['1', '2', '3'].map((tier) => <option key={tier} value={tier}>{t('abm.tierN', { values: { tier } })}</option>)}
            </select>
            <Button size="small" type="submit" data-account-target-add disabled={add.isPending}>{t('abm.add')}</Button>
          </form>
        ) : null}
        {add.error ? <p className="text-dense text-danger-text">{errorText(add.error as ApiError)}</p> : null}
      </div>
    </Panel>
  );
}
